import type { TFunction } from 'i18next';

import { GridRecord } from '../../components/Grid/GridDataTypes';
import {
  CreateUpdateSurveyDto,
  CreateUpdateSurveyDtoFromJSON,
  CreateUpdateQuestionDto,
  CreateUpdateQuestionDtoFromJSON,
  DefaultApi,
  GetLastEditorDto,
  GetQuestionDto,
  GetSurveyDto,
  GetSurveyForClientDto,
  SubmitAnswersDto,
  GetSurveyAnswersByClientSurveyIdDto,
  GetSurveyEmailContentDto,
  CreateUpdateSurveyEmailContentDto,
  GetEmailPreviewDto,
  SetEmailBodyDto,
} from '../autogenerated/pokApiClient';
import { TKeys } from '../../hooks/useTranslation';
import { surveyForGrid } from '../../../pok/components/Surveys/SurveySearch';
import {
  SurveySchema,
  QuestionSchema,
  SurveyEmailContentSchema,
} from '../validation/schemas';
import { validateAgainst } from '../validation/validateAgainst';
import { QuestionResponseTypeEnum } from '../validation/enums';

export interface SurveyContextInterface {
  getById: (id: string) => Promise<GetSurveyDto>;
  getAllByPortion: (
    t: TFunction<'administrationLng', 'administrationLng'>,
    tk: TKeys<'administrationLng'>,
    lang: string,
    pageSize: number,
    pageNumber: number,
    orderBy?: string,
    orderDirection?: string,
    filterText?: string,
    filterColumns?: string,
    onlyMy?: string,
    onlyDraft?: string,
    onlyActive?: string,
  ) => Promise<GridRecord[]>;
  getAllCount: (
    filterText?: string,
    filterColumns?: string,
    onlyMy?: string,
    onlyDraft?: string,
    onlyActive?: string,
  ) => Promise<number>;
  create: (dto: CreateUpdateSurveyDto) => Promise<GetSurveyDto>;
  update: (id: string, dto: CreateUpdateSurveyDto) => Promise<void>;
  updateQuestions: (
    id: string,
    questions: CreateUpdateQuestionDto[],
  ) => Promise<void>;
  getLastEditor: (id: string) => Promise<GetLastEditorDto>;
  getQuestionLastEditor: (id: string) => Promise<GetLastEditorDto>;
  getByToken: (token: string) => Promise<GetSurveyForClientDto>;
  preview: (id: string) => Promise<GetSurveyForClientDto>;
  submitAnswers: (token: string, answers: SubmitAnswersDto) => Promise<void>;
  getQuestionsWithAnswers: (
    clientSurveyId: string,
  ) => Promise<GetSurveyAnswersByClientSurveyIdDto>;
  getEmailTemplate: (surveyId: string) => Promise<GetSurveyEmailContentDto>;
  updateEmailTemplate: (
    surveyId: string,
    data: CreateUpdateSurveyEmailContentDto,
  ) => Promise<GetSurveyEmailContentDto>;
  getAllByNamePattern: (pattern: string) => Promise<GetSurveyDto[]>;
  previewEmail: (content: SetEmailBodyDto) => Promise<GetEmailPreviewDto>;
}

export const SurveyContext = (api: DefaultApi) => {
  return {
    getById: (id: string) => api.surveyControllerGet(id),

    getAllByNamePattern: (pattern: string) =>
      api.surveyControllerGetAllByNamePattern(pattern),

    getAllByPortion: async (
      t: TFunction<'administrationLng', 'administrationLng'>,
      tk: TKeys<'administrationLng'>,
      lang: string,
      pageSize: number,
      pageNumber: number,
      orderBy?: string,
      orderDirection?: string,
      filterText?: string,
      filterColumns?: string,
      onlyMy?: string,
      onlyDraft?: string,
      onlyActive?: string,
    ) => {
      const data = await api.surveyControllerGetAllByPortion(
        pageSize,
        pageNumber,
        orderBy || '',
        orderDirection || '',
        filterText || '',
        filterColumns,
        onlyMy,
        onlyDraft,
        onlyActive,
      );

      return data.map(item => surveyForGrid(t, tk, item, lang));
    },

    getAllCount: (
      filterText?: string,
      filterColumns?: string,
      onlyMy?: string,
      onlyDraft?: string,
      onlyActive?: string,
    ) =>
      api.surveyControllerGetAllCount(
        filterText || '',
        filterColumns || '{}',
        onlyMy,
        onlyDraft,
        onlyActive,
      ),

    create: (survey: CreateUpdateSurveyDto) =>
      api.surveyControllerCreate(survey),

    update: (id: string, survey: CreateUpdateSurveyDto) =>
      api.surveyControllerUpdate(id, survey),

    updateQuestions: (id: string, questions: CreateUpdateQuestionDto[]) =>
      api.surveyControllerUpdateQuestions(id, { questions }),

    getLastEditor: (id: string) => api.surveyControllerGetLastEditor(id),

    getQuestionLastEditor: (id: string) =>
      api.questionControllerGetLastEditor(id),

    getByToken: (token: string) => api.surveyControllerGetByToken(token),

    preview: (id: string) => api.surveyControllerPreviewById(id),

    submitAnswers: (token: string, answers: SubmitAnswersDto) =>
      api.surveyAnswerControllerSubmitAnswers(token, answers),

    getQuestionsWithAnswers: (clientSurveyId: string) =>
      api.clientSurveyControllerGetQuestionsWithAnswers(clientSurveyId),

    getEmailTemplate: async (surveyId: string) =>
      api.surveyControllerGetEmailContent(surveyId),

    updateEmailTemplate: async (
      surveyId: string,
      data: CreateUpdateSurveyEmailContentDto,
    ) => api.surveyControllerUpdateEmailContent(surveyId, data),
    previewEmail: async (content: SetEmailBodyDto) =>
      api.surveyControllerPreviewEmail(content),
  };
};

export const newSurvey = (userId?: string): CreateUpdateSurveyDto => {
  return CreateUpdateSurveyDtoFromJSON({
    validityPeriodDays: 7,
    remind: true,
    active: true,
    employeeIds: userId ? [userId] : undefined,
    contactTypeId: null,
    sendToGeneral: false,
    clientGroupId: null,
  });
};

export const newQuestion = (
  surveyId: string,
  order: number,
): CreateUpdateQuestionDto => {
  return CreateUpdateQuestionDtoFromJSON({
    surveyId,
    order,
    question: '',
    responseType: QuestionResponseTypeEnum.Text,
    optional: false,
    expectedYesNoAnswer: null,
    blockOnWrongAnswer: false,
    warningLight: false,
    active: true,
  });
};

export const convertSurvey = (survey: GetSurveyDto): CreateUpdateSurveyDto => {
  const converted = CreateUpdateSurveyDtoFromJSON(survey);
  if (survey.surveyCompanies) {
    converted.companyIds = survey.surveyCompanies.map(sc => sc.companyId);
  }
  if (survey.surveyEmployees) {
    converted.employeeIds = survey.surveyEmployees.map(se => se.employeeId);
  }
  converted.contactTypeId = survey.contactType?.id ?? null;
  converted.sendToGeneral = survey.sendToGeneral ?? false;
  converted.clientGroupId = survey.clientGroupId ?? null;
  return converted;
};

export const convertQuestion = (
  question: GetQuestionDto,
): CreateUpdateQuestionDto => {
  return CreateUpdateQuestionDtoFromJSON(question);
};

export const validate = (survey: CreateUpdateSurveyDto) =>
  validateAgainst(SurveySchema, survey);

export const validateQuestion = (question: CreateUpdateQuestionDto) =>
  validateAgainst(QuestionSchema, question);

export const validateEmailData = (
  emailData: CreateUpdateSurveyEmailContentDto,
) => validateAgainst(SurveyEmailContentSchema, emailData);
